import { z } from 'zod'

/**
 * CNPJ alfanumérico — IN RFB nº 2.229/2024.
 *
 * Cópia do contrato `doc-cnpj` do _padrao-via (implementação canônica em
 * `Fiscal Hub/shared/cnpj.ts`). Mudou lá, muda aqui — até existir o via-nucleo.
 *
 *     AA.AAA.AAA/AAAA-DD
 *     └ 12 posições alfanuméricas ┘└ 2 DV numéricos
 *
 * DV: módulo 11, pesos 2..9, valor do caractere = código ASCII − 48
 * ('0'→0 … '9'→9, 'A'→17 … 'Z'→42). CNPJ só numérico valida pela mesma conta.
 *
 * REGRA: nunca remover "tudo que não é dígito" de um CNPJ — isso apaga as letras,
 * e a comparação emitente × empresa (que decide ENTRADA × SAÍDA) passa a falhar
 * em silêncio para toda empresa com CNPJ novo.
 */

export function normalizarCnpj(valor: string | null | undefined): string {
  if (!valor) return ''
  return valor.toUpperCase().replace(/[^0-9A-Z]/g, '')
}

export function normalizarCpf(valor: string | null | undefined): string {
  if (!valor) return ''
  return valor.replace(/[^0-9]/g, '')
}

/** CPF (11) ou CNPJ (14), sem máscara, CNPJ em maiúsculas. */
export function normalizarDocumento(valor: string | null | undefined): string {
  return normalizarCnpj(valor)
}

export function isCnpjAlfanumerico(valor: string | null | undefined): boolean {
  const limpo = normalizarCnpj(valor)
  return limpo.length === 14 && /[A-Z]/.test(limpo.slice(0, 12))
}

function digitoModulo11(base: string): number {
  let soma = 0
  let peso = 2
  for (let i = base.length - 1; i >= 0; i--) {
    soma += (base.charCodeAt(i) - 48) * peso
    peso = peso === 9 ? 2 : peso + 1
  }
  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

export function calcularDigitosCnpj(base12: string): string {
  const base = normalizarCnpj(base12)
  if (base.length !== 12) throw new Error('A base do CNPJ deve ter 12 posições')
  const dv1 = digitoModulo11(base)
  const dv2 = digitoModulo11(`${base}${dv1}`)
  return `${dv1}${dv2}`
}

export function validarCnpj(valor: string | null | undefined): boolean {
  const limpo = normalizarCnpj(valor)
  if (!/^[0-9A-Z]{12}\d{2}$/.test(limpo)) return false
  const base = limpo.slice(0, 12)
  if (/^(.)\1{11}$/.test(base)) return false
  return calcularDigitosCnpj(base) === limpo.slice(12)
}

export function validarCpf(valor: string | null | undefined): boolean {
  const limpo = normalizarCpf(valor)
  if (limpo.length !== 11 || /^(\d)\1{10}$/.test(limpo)) return false
  const digito = (tamanho: number) => {
    let soma = 0
    for (let i = 0; i < tamanho; i++) soma += Number(limpo[i]) * (tamanho + 1 - i)
    const resto = (soma * 10) % 11
    return resto === 10 ? 0 : resto
  }
  return digito(9) === Number(limpo[9]) && digito(10) === Number(limpo[10])
}

/**
 * Raiz do CNPJ: as 8 primeiras posições identificam a pessoa jurídica; matriz e
 * filiais compartilham a raiz e diferem na ordem (posições 9–12). É a chave que
 * unifica as unidades numa empresa só no Via Tax (docs/adr/0005). Vale igual
 * para o CNPJ alfanumérico.
 */
export function raizCnpj(valor: string | null | undefined): string {
  return normalizarCnpj(valor).slice(0, 8)
}

export function ordemCnpj(valor: string | null | undefined): string {
  return normalizarCnpj(valor).slice(8, 12)
}

/** Ordem 0001 é a matriz por convenção da RFB. */
export function isMatriz(valor: string | null | undefined): boolean {
  return ordemCnpj(valor) === '0001'
}

export function formatarRaizCnpj(valor: string | null | undefined): string {
  const r = raizCnpj(valor)
  return r.length === 8 ? `${r.slice(0, 2)}.${r.slice(2, 5)}.${r.slice(5, 8)}` : (valor ?? '')
}

export function formatarCnpj(valor: string | null | undefined): string {
  const l = normalizarCnpj(valor)
  if (l.length !== 14) return valor ?? ''
  return `${l.slice(0, 2)}.${l.slice(2, 5)}.${l.slice(5, 8)}/${l.slice(8, 12)}-${l.slice(12)}`
}

/** Schema Zod: aceita com ou sem máscara, entrega normalizado e validado. */
export const Cnpj = z
  .string()
  .transform((v) => normalizarCnpj(v))
  .refine((v) => validarCnpj(v), { message: 'CNPJ inválido' })
